import { useEffect, useMemo, useState } from "react"
import { X, Download } from "lucide-react"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import {
  Field,
  Input,
  ErrorBanner,
  inputStyle,
  labelStyle,
  formatApiError,
} from "@/app/components/FormPrimitives"
import { secondaryButton, primaryButton } from "@/app/lib/buttonStyles"
import {
  useParties,
  useAssignorSettings,
  useGenerateAssignment,
  downloadAssignmentDocument,
  type AssignmentInterest,
  type AssignmentDocument,
} from "@/app/api/assignments"
import type { Lease } from "@/app/api/leases"
import type { ClientTheme } from "@/app/lib/themes"

// Township → range → section, matching AssignmentDocBuilder's ordering so the
// per-STR inputs read in the same order as Exhibit B.
function strSortKey(str: string): [number, string, number, string, number] {
  const m = /(\d+)\s*-\s*(\d+)\s*([NS])\s*-\s*(\d+)\s*([EW])/i.exec(str)
  if (!m) return [9999, "Z", 9999, "Z", 9999]
  return [Number(m[2]), m[3].toUpperCase(), Number(m[4]), m[5].toUpperCase(), Number(m[1])]
}

function compareStrs(a: string, b: string): number {
  const ka = strSortKey(a)
  const kb = strSortKey(b)
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] < kb[i]) return -1
    if (ka[i] > kb[i]) return 1
  }
  return a.localeCompare(b)
}

// Create Assignment dialog — generates one recordable .doc per county from the
// selected leases (Brandon's prototype panel). Documents are rendered
// server-side; this form only collects the terms.
export function CreateAssignmentModal({
  leases,
  onClose,
  theme,
}: {
  leases: Lease[]
  onClose: () => void
  theme: ClientTheme
}) {
  const isMobile = useIsMobile()
  const { data: parties } = useParties()
  const { data: assignor } = useAssignorSettings()
  const generate = useGenerateAssignment()

  const [assigneeName, setAssigneeName] = useState("")
  const [assigneeAddress, setAssigneeAddress] = useState("")
  const [effectiveOn, setEffectiveOn] = useState("")
  const [signer, setSigner] = useState("")
  const [signerTitle, setSignerTitle] = useState("")
  const [interest, setInterest] = useState<AssignmentInterest>("arti")
  const [pct, setPct] = useState("")
  const [netAcres, setNetAcres] = useState("")
  const [perStr, setPerStr] = useState<Record<string, string>>({})
  const [nriOn, setNriOn] = useState(false)
  const [nriPct, setNriPct] = useState("")
  const [docs, setDocs] = useState<AssignmentDocument[]>([])

  useEffect(() => {
    if (!assignor) return
    setSigner((s) => s || assignor.signer || "")
    setSignerTitle((s) => s || assignor.signer_title || "")
  }, [assignor])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])

  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [])

  const strs = useMemo(
    () => [...new Set(leases.map((l) => l.str).filter((s): s is string => !!s))].sort(compareStrs),
    [leases]
  )
  const counties = useMemo(() => [...new Set(leases.map((l) => l.county))].sort(), [leases])
  const nriValue = parseFloat(nriPct)
  const nriValid = !isNaN(nriValue) && nriValue > 0 && nriValue < 100

  function onAssigneeChange(name: string) {
    setAssigneeName(name)
    const hit = parties?.find((p) => p.name.toLowerCase() === name.trim().toLowerCase())
    if (hit?.address) setAssigneeAddress(hit.address)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    generate.mutate(
      {
        lease_ids: leases.map((l) => l.id),
        assignee_name: assigneeName.trim(),
        assignee_address: assigneeAddress.trim(),
        effective_on: effectiveOn || null,
        interest,
        pct: interest === "pct" ? pct : undefined,
        net_acres: interest === "netacres" && strs.length <= 1 ? netAcres : undefined,
        per_str: interest === "netacres" && strs.length > 1 ? perStr : undefined,
        nri_pct: nriOn ? nriPct : undefined,
        signer: signer.trim(),
        signer_title: signerTitle.trim(),
      },
      { onSuccess: (res) => setDocs(res.documents) }
    )
  }

  const canGenerate = !!assigneeName.trim() && !!effectiveOn && !generate.isPending
  const radioRow: React.CSSProperties = {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    color: theme.textPrimary,
  }
  const hintStyle: React.CSSProperties = {
    fontFamily: "var(--font-ui)",
    fontSize: "12px",
    color: theme.textDim,
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center ${isMobile ? "items-stretch" : "items-center p-4"}`}
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className={`relative flex flex-col overflow-y-auto ${isMobile ? "" : "rounded-2xl"}`}
        style={{
          backgroundColor: theme.bgSurface,
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          width: isMobile ? "100vw" : "min(720px, 95vw)",
          height: isMobile ? "100dvh" : undefined,
          maxHeight: isMobile ? undefined : "90vh",
        }}
      >
        <div
          className="flex items-center justify-between px-6 pt-5 pb-4"
          style={{ borderBottom: `1px solid ${theme.borderColor}` }}
        >
          <div className="flex flex-col">
            <span
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "15px",
                fontWeight: 600,
                color: theme.textPrimary,
              }}
            >
              Create Assignment
            </span>
            <span style={hintStyle}>
              {leases.length} lease{leases.length === 1 ? "" : "s"} · {counties.join(", ")}
              {assignor?.legal_name ? ` · Assignor: ${assignor.legal_name}` : ""}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme.textMuted,
              padding: "4px",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            <Field label="Assignee" theme={theme} required>
              <input
                list="assignment-party-list"
                value={assigneeName}
                onChange={(e) => onAssigneeChange(e.target.value)}
                placeholder="Cherokee Moon, LLC"
                style={inputStyle(theme)}
              />
              <datalist id="assignment-party-list">
                {(parties ?? []).map((p) => (
                  <option key={p.id} value={p.name} />
                ))}
              </datalist>
            </Field>
            <Field label="Effective Date" theme={theme} required>
              <Input type="date" value={effectiveOn} onChange={setEffectiveOn} theme={theme} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Assignee Address" theme={theme}>
                <Input
                  value={assigneeAddress}
                  onChange={setAssigneeAddress}
                  placeholder="6701 Broadway Extension, Suite 310, Oklahoma City, OK 73116"
                  theme={theme}
                />
              </Field>
            </div>
            <Field label="Assignor Signer" theme={theme}>
              <Input
                value={signer}
                onChange={setSigner}
                placeholder="Hunter Miller"
                theme={theme}
              />
            </Field>
            <Field label="Signer Title" theme={theme}>
              <Input
                value={signerTitle}
                onChange={setSignerTitle}
                placeholder="Manager"
                theme={theme}
              />
            </Field>
          </div>

          <div className="flex flex-col gap-2">
            <span style={labelStyle(theme)}>Interest Conveyed</span>
            <div style={radioRow}>
              {(
                [
                  ["arti", "All right, title & interest"],
                  ["netacres", "Specified net leasehold acres"],
                  ["pct", "Percentage of interest"],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  className="flex items-center gap-1.5"
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="radio"
                    name="assignment-interest"
                    checked={interest === value}
                    onChange={() => setInterest(value)}
                  />
                  {label}
                </label>
              ))}
            </div>

            {interest === "pct" && (
              <div style={{ maxWidth: "220px" }}>
                <Field label="Percentage Conveyed (%)" theme={theme}>
                  <Input value={pct} onChange={setPct} placeholder="50" theme={theme} />
                </Field>
              </div>
            )}
            {interest === "netacres" && strs.length <= 1 && (
              <div style={{ maxWidth: "260px" }}>
                <Field label="Net Leasehold Acres Assigned" theme={theme}>
                  <Input
                    value={netAcres}
                    onChange={setNetAcres}
                    placeholder="160.00"
                    theme={theme}
                  />
                </Field>
              </div>
            )}
            {interest === "netacres" && strs.length > 1 && (
              <div className="flex flex-col gap-2">
                <span style={hintStyle}>
                  Selection spans {strs.length} STRs — net acres per STR print as Exhibit “B”.
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3">
                  {strs.map((s) => (
                    <Field key={s} label={`${s} — net acres`} theme={theme}>
                      <Input
                        value={perStr[s] ?? ""}
                        onChange={(v) => setPerStr((p) => ({ ...p, [s]: v }))}
                        placeholder="80.00"
                        theme={theme}
                      />
                    </Field>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <span style={labelStyle(theme)}>Specific NRI Delivered?</span>
            <div style={radioRow}>
              <label className="flex items-center gap-1.5" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  name="assignment-nri"
                  checked={!nriOn}
                  onChange={() => setNriOn(false)}
                />
                No
              </label>
              <label className="flex items-center gap-1.5" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  name="assignment-nri"
                  checked={nriOn}
                  onChange={() => setNriOn(true)}
                />
                Yes — Assignor reserves an ORRI to deliver a stated NRI
              </label>
            </div>
            {nriOn && (
              <div style={{ maxWidth: "260px" }} className="flex flex-col gap-1">
                <Field label="Net Revenue Interest Delivered (%)" theme={theme}>
                  <Input value={nriPct} onChange={setNriPct} placeholder="77.50" theme={theme} />
                </Field>
                {nriPct && (
                  <span style={hintStyle}>
                    {nriValid
                      ? `ORRI reserved = difference between existing burdens and ${(100 - nriValue).toFixed(2)}%`
                      : "Enter a percentage between 0 and 100 — otherwise no reservation is added."}
                  </span>
                )}
              </div>
            )}
          </div>

          {generate.isError && (
            <ErrorBanner theme={theme} message={formatApiError(generate.error)} />
          )}

          {docs.length > 0 && (
            <div
              className="rounded-lg p-4 flex flex-col gap-2"
              style={{
                backgroundColor: theme.bgSurface2,
                border: `1px solid ${theme.borderColor}`,
              }}
            >
              <span style={{ ...labelStyle(theme), color: theme.brandTeal }}>
                Assignment{docs.length > 1 ? "s" : ""} generated
                {docs.length > 1 ? ` — one per county (${docs.length})` : ""}
              </span>
              {docs.map((d) => (
                <div key={d.county} className="flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => downloadAssignmentDocument(d)}
                    className="btn"
                    style={primaryButton(theme)}
                  >
                    <Download size={14} /> {d.county} County
                  </button>
                  <span style={hintStyle}>
                    {d.lease_count} lease{d.lease_count === 1 ? "" : "s"} · body + Exhibit A
                    {d.has_exhibit_b ? " + Exhibit “B” (net acres)" : ""}
                  </span>
                </div>
              ))}
              <span style={hintStyle}>
                Portrait body (2" top recording margin) with landscape Exhibit A. Opens in Microsoft
                Word.
              </span>
            </div>
          )}
        </div>

        <div className="px-6 pb-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="btn w-full sm:w-auto"
            style={{ ...secondaryButton(theme), justifyContent: "center" }}
          >
            {docs.length > 0 ? "Done" : "Cancel"}
          </button>
          <button
            type="submit"
            disabled={!canGenerate}
            className="btn w-full sm:w-auto"
            style={{
              ...primaryButton(theme),
              justifyContent: "center",
              opacity: canGenerate ? 1 : 0.6,
              cursor: canGenerate ? "pointer" : "default",
            }}
            title={canGenerate ? undefined : "Assignee and effective date are required"}
          >
            {generate.isPending
              ? "Generating…"
              : `${docs.length > 0 ? "Regenerate" : "Generate"} Assignment${counties.length > 1 ? "s" : ""}`}
          </button>
        </div>
      </form>
    </div>
  )
}
